import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
    AlertTriangle, Check, CheckCircle2, Clock, Copy, FileDown, Info, Loader2, PenLine, QrCode, RotateCcw, Upload, X,
} from 'lucide-react';
import { vorankKartenStatus } from '../../services/vorank/vorankUnterschriftService';
import { vorankLinkUrl, type VorankFassungZeile } from '../../services/vorank/vorankUnterschriftTypes';
import { describeError } from '../../services/errorMessage';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { VorankEinlesenDialog } from './VorankEinlesenDialog';
import { legeDigitaleUnterschriftenAb, ladeUnterschriebenePdfHerunter } from './vorankUnterschriftAblauf';
import {
    vorankDigitalOhneAblage,
    vorankEinleseKandidaten,
    vorankKarte,
    vorankTag,
    type VorankKartenTon,
} from './vorankUnterschriftAnsicht';

interface Props {
    /** Belastbare Projekt-Id (`belastbareProjektId`) oder null. */
    projektId: string | null;
    /**
     * Fassungen des Projekts (`ladeFassungen`). `null`: noch nicht geladen
     * oder nicht verfügbar (SQL fehlt, Netz) — dann zeigt die Karte nichts
     * (fail-open, der Export läuft trotzdem).
     */
    fassungen: VorankFassungZeile[] | null;
    /** `vorankHash(data)` des Editorstands. */
    aktuellerHash: string;
    isReadOnly: boolean;
    /** Fassungen neu lesen — nach eigenen Aktionen, kein Dauer-Polling. */
    onNeuLaden: () => void;
}

/**
 * Digital unterschriebene Fassungen, deren Ablage in dieser Sitzung schon
 * versucht wurde (Plan §7) — modulweit, damit ein Reiterwechsel (die Karte
 * wird neu eingehängt) nicht erneut ablegt. „Erneut versuchen“ nimmt sie
 * wieder heraus.
 */
const digitalVersucht = new Set<string>();

const TON: Record<VorankKartenTon, { rahmen: string; icon: React.ReactNode }> = {
    neutral: { rahmen: 'bg-white border-slate-200', icon: <PenLine className="w-4 h-4 text-slate-400" /> },
    warten: { rahmen: 'bg-blue-50/50 border-blue-200', icon: <Clock className="w-4 h-4 text-blue-600" /> },
    korrektur: { rahmen: 'bg-amber-50 border-amber-200', icon: <AlertTriangle className="w-4 h-4 text-amber-600" /> },
    ok: { rahmen: 'bg-emerald-50 border-emerald-200', icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" /> },
    veraltet: { rahmen: 'bg-amber-50 border-amber-200', icon: <AlertTriangle className="w-4 h-4 text-amber-600" /> },
};

const KNOPF = 'min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 '
    + 'hover:bg-slate-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition';

/**
 * Statuskarte „Unterschrift des Bauherrn“ im Vorankündigungs-Reiter (Plan §8,
 * §9): keine Fassung / wartet (Link, QR) / Korrektur gewünscht / unterschrieben
 * / unterschrieben, aber seither geändert. Dazu „Unterschriebene PDF
 * herunterladen“ (für die Behörde) und „Unterschriebene Datei einlesen“
 * (Papierweg). Legt digital Unterschriebenes selbst in „Dokumente“ ab (§7).
 * Die Regeln stehen in `vorankUnterschriftAnsicht.ts`.
 */
export const VorankUnterschriftKarte: React.FC<Props> = ({ projektId, fassungen, aktuellerHash, isReadOnly, onNeuLaden }) => {
    const [jetzt] = useState(() => new Date());
    const [qr, setQr] = useState<{ url: string; bild: string } | null>(null);
    const [qrOffen, setQrOffen] = useState(false);
    const [kopiert, setKopiert] = useState(false);
    const [laedt, setLaedt] = useState(false);
    const [einlesenOffen, setEinlesenOffen] = useState(false);
    const [meldung, setMeldung] = useState<{ ton: 'fehler' | 'info'; text: string } | null>(null);
    const [ablageFehler, setAblageFehler] = useState<string[]>([]);

    // Digital unterschrieben, PDF noch nicht abgelegt → erzeugen, ablegen,
    // vermerken (Plan §7). Einmal je Fassung und Sitzung; gesetzt wird erst in
    // der Antwort, nie synchron im Effekt.
    useEffect(() => {
        if (isReadOnly || !projektId || !fassungen) return;
        const offen = vorankDigitalOhneAblage(fassungen).filter(f => !digitalVersucht.has(f.id));
        if (!offen.length) return;
        offen.forEach(f => digitalVersucht.add(f.id));
        void legeDigitaleUnterschriftenAb(projektId, offen).then(fehler => {
            setAblageFehler(fehler);
            if (fehler.length < offen.length) onNeuLaden();
        });
    }, [fassungen, projektId, isReadOnly, onNeuLaden]);

    if (!fassungen) return null;

    const karte = vorankKarte({ status: vorankKartenStatus(fassungen, aktuellerHash), fassungen, aktuellerHash, jetzt });
    const ton = TON[karte.ton];
    const linkFassung = karte.linkFassung;
    const pdfFassung = karte.pdfFassung;
    const url = linkFassung ? vorankLinkUrl(window.location.origin, window.location.pathname, linkFassung.token) : null;
    const kandidaten = vorankEinleseKandidaten(fassungen).length > 0;
    const kannEinlesen = !isReadOnly && !!projektId && kandidaten;

    const kopieren = () => {
        if (!url) return;
        const schreiben = navigator.clipboard?.writeText(url);
        if (!schreiben) {
            setMeldung({ ton: 'fehler', text: 'Kopieren nicht möglich — den Link unten markieren und kopieren.' });
            return;
        }
        void schreiben.then(
            () => {
                setKopiert(true);
                window.setTimeout(() => setKopiert(false), 2000);
            },
            () => setMeldung({ ton: 'fehler', text: 'Kopieren nicht möglich — den Link unten markieren und kopieren.' }),
        );
    };

    const qrUmschalten = () => {
        if (!url) return;
        if (qrOffen) {
            setQrOffen(false);
            return;
        }
        setQrOffen(true);
        if (qr?.url === url) return;
        void QRCode.toDataURL(url, { width: 360, margin: 2 }).then(
            bild => setQr({ url, bild }),
            () => setMeldung({ ton: 'fehler', text: 'Der QR-Code ließ sich nicht erzeugen — bitte den Link kopieren.' }),
        );
    };

    const herunterladen = () => {
        if (!pdfFassung || laedt) return;
        setLaedt(true);
        setMeldung(null);
        void ladeUnterschriebenePdfHerunter(pdfFassung).then(
            () => setLaedt(false),
            (e: unknown) => {
                setLaedt(false);
                setMeldung({ ton: 'fehler', text: `Die unterschriebene PDF ließ sich nicht herunterladen: ${describeError(e)}` });
            },
        );
    };

    const ablageErneut = () => {
        vorankDigitalOhneAblage(fassungen).forEach(f => digitalVersucht.delete(f.id));
        setAblageFehler([]);
        onNeuLaden();
    };

    const nachEinlesen = (bereits: boolean) => {
        setEinlesenOffen(false);
        setMeldung(bereits
            ? { ton: 'info', text: 'Die Fassung war schon als unterschrieben vermerkt (anderes Fenster?). Die eben abgelegten Dateien bleiben in Dokumente.' }
            : { ton: 'info', text: 'Eingelesen: die unterschriebene Fassung liegt in Dokumente und gilt jetzt für Plot und Behörde.' });
        onNeuLaden();
    };

    return (
        <section className={`mb-4 border rounded-xl px-4 py-3 print:hidden ${ton.rahmen}`} aria-label="Unterschrift des Bauherrn">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <span className="mt-0.5 shrink-0">{ton.icon}</span>
                    <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Unterschrift des Bauherrn</p>
                        <p className="text-sm font-bold text-slate-800">{karte.titel}</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {pdfFassung && (
                        <button type="button" onClick={herunterladen} disabled={laedt} className={KNOPF}
                            title="Die unterschriebene Vorankündigung ohne Begleitblatt — für die Behörde">
                            {laedt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                            Unterschriebene PDF herunterladen
                        </button>
                    )}
                    {kannEinlesen && (
                        <button type="button" onClick={() => setEinlesenOffen(true)} className={KNOPF}
                            title="Eine per Mail oder Post zurückgekommene, unterschriebene Vorankündigung (PDF oder Foto) einlesen">
                            <Upload className="w-3.5 h-3.5" /> Unterschriebene Datei einlesen
                        </button>
                    )}
                    {isReadOnly && kandidaten && (
                        <button type="button" disabled className={KNOPF} title={readOnlyTitle('Eine unterschriebene Datei lässt sich nicht einlesen.')}>
                            <Upload className="w-3.5 h-3.5" /> Unterschriebene Datei einlesen
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-2 pl-6 space-y-1 text-xs text-slate-700">
                {karte.zeilen.map((z, i) => <p key={i} className="break-words">{z}</p>)}
                {karte.zitat && (
                    <blockquote className="border-l-2 border-amber-300 pl-2 py-0.5 text-slate-800 whitespace-pre-line break-words">
                        {karte.zitat}
                    </blockquote>
                )}
            </div>

            {url && (
                <div className="mt-2 pl-6 space-y-2">
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={kopieren} className={KNOPF}>
                            {kopiert ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                            {kopiert ? 'Kopiert' : 'Link kopieren'}
                        </button>
                        <button type="button" onClick={qrUmschalten} className={KNOPF}>
                            <QrCode className="w-3.5 h-3.5" /> {qrOffen ? 'QR ausblenden' : 'QR anzeigen'}
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-500 break-all select-all">{url}</p>
                    {qrOffen && (
                        <div className="flex items-start gap-3">
                            {qr?.url === url
                                ? <img src={qr.bild} alt="QR-Code zum Unterschrift-Link" className="w-40 h-40 rounded-lg border border-slate-200 bg-white" />
                                : <div className="w-40 h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
                            {linkFassung && (
                                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> gültig bis {vorankTag(linkFassung.gueltigBis)}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {ablageFehler.length > 0 && (
                <div className="mt-2 pl-6 flex items-start gap-2 text-[11px] text-rose-700">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                    <span className="flex-1 min-w-0 break-words">{ablageFehler.join(' ')}</span>
                    <button type="button" onClick={ablageErneut}
                        className="shrink-0 inline-flex items-center gap-1 font-bold underline">
                        <RotateCcw className="w-3 h-3" /> Erneut versuchen
                    </button>
                </div>
            )}

            {meldung && (
                <div className={`mt-2 pl-6 flex items-start gap-2 text-[11px] ${meldung.ton === 'fehler' ? 'text-rose-700' : 'text-slate-600'}`}>
                    {meldung.ton === 'fehler' ? <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> : <Info className="w-3.5 h-3.5 shrink-0 mt-px" />}
                    <span className="flex-1 min-w-0 break-words">{meldung.text}</span>
                    <button type="button" onClick={() => setMeldung(null)} title="Hinweis ausblenden" className="shrink-0 p-0.5 rounded hover:bg-black/5">
                        <X className="w-3 h-3" />
                    </button>
                </div>
            )}

            {einlesenOffen && projektId && (
                <VorankEinlesenDialog
                    projektId={projektId}
                    fassungen={fassungen}
                    onSchliessen={() => setEinlesenOffen(false)}
                    onEingelesen={nachEinlesen}
                />
            )}
        </section>
    );
};
